import React, { FC } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { X } from "lucide-react";
import { RootState } from "../../../../redux/app/store";
import { GeneralSettings } from "../../../../redux/features/types/settingsTypes";
import { convertToUnit } from "../../../../helpers";
import { prettifyPaymentOptionName } from "../PaymentMethods/utils";

interface ViewGeneralSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const formatNaira = (raw: number | string | null | undefined): string => {
  const n = typeof raw === "string" ? Number(raw) : raw;
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const formatPercent = (raw: number | null | undefined): string => {
  if (raw == null || Number.isNaN(raw)) return "—";
  return `${raw}%`;
};

const formatInt = (raw: number | null | undefined): string => {
  if (raw == null || Number.isNaN(raw)) return "—";
  return String(raw);
};

/** Turn a seconds count into a human-readable string ("2 days", "100 seconds"). */
const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  const { value, unit } = convertToUnit(seconds);
  const pretty = unit === "seconds" ? "second" : unit.slice(0, -1); // "days" -> "day"
  return `${value} ${pretty}${value === 1 ? "" : "s"}`;
};

const formatBool = (val: boolean | undefined): string =>
  val ? "Enabled" : "Disabled";

// ---------------------------------------------------------------------------
// Small atoms
// ---------------------------------------------------------------------------

const SectionHeading: FC<{ title: string }> = ({ title }) => (
  <h3 className="text-sm font-semibold text-gray-900 mb-2 mt-5 first:mt-0">
    {title}
  </h3>
);

const Row: FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex justify-between items-baseline gap-3 py-1.5 border-b border-gray-100 last:border-b-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-800 text-right">
      {value}
    </span>
  </div>
);

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const isActive = status === "ACTIVE";
  return (
    <span
      className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
        isActive
          ? "bg-green-100 text-green-700"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
};

const BoolBadge: FC<{ enabled: boolean | undefined }> = ({ enabled }) => (
  <span
    className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
      enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {formatBool(enabled)}
  </span>
);

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const ViewGeneralSettingsModal: React.FC<ViewGeneralSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const data = useSelector(
    (state: RootState) => state.settings.general
  ) as GeneralSettings | null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold">General Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto">
          {!data ? (
            <p className="text-sm text-gray-500">
              Settings have not loaded yet. Close and try again, or refresh
              the page.
            </p>
          ) : (
            <>
              {/* Lifecycle ------------------------------------------- */}
              <SectionHeading title="Lifecycle" />
              <Row
                label="Status"
                value={<StatusBadge status={data.status} />}
              />
              <Row
                label="Last updated"
                value={
                  data.updatedAt
                    ? moment(data.updatedAt).format("Do MMM YYYY, h:mm A")
                    : "—"
                }
              />
              <Row
                label="Created"
                value={
                  data.createdAt
                    ? moment(data.createdAt).format("Do MMM YYYY")
                    : "—"
                }
              />

              {/* Durations ------------------------------------------- */}
              <SectionHeading title="Durations" />
              <Row
                label="Payment duration"
                value={formatDuration(data.paymentDuration)}
              />
              <Row
                label="Dispute appeal duration"
                value={formatDuration(data.disputeAppealDuration)}
              />
              <Row
                label="Accept initial order duration"
                value={formatDuration(data.acceptInitialOrderDuration)}
              />
              <Row
                label="Accept order duration"
                value={formatDuration(data.acceptOrderDuration)}
              />
              <Row
                label="Request return duration"
                value={formatDuration(data.requestReturnDuration)}
              />
              <Row
                label="Dispatch order waiting duration"
                value={formatDuration(data.dispatchOrderWaitingDuration)}
              />
              <Row
                label="Buyer request batch timeout"
                value={formatDuration(data.buyerRequestBatchTimeout)}
              />
              <Row
                label="Buyer request expiry timeout"
                value={formatDuration(data.buyerRequestExpiryTimeout)}
              />

              {/* Providers ------------------------------------------- */}
              <SectionHeading title="Default providers" />
              <Row
                label="Payment"
                value={prettifyPaymentOptionName(
                  data.defaultPaymentProvider as string
                )}
              />
              <Row
                label="BVN verify"
                value={prettifyPaymentOptionName(
                  data.defaultBVNVerifyProvider as string
                )}
              />
              <Row
                label="NIN verify"
                value={prettifyPaymentOptionName(
                  data.defaultNINVerifyProvider as string
                )}
              />
              <Row
                label="CAC verify"
                value={prettifyPaymentOptionName(
                  data.defaultCACVerifyProvider as string
                )}
              />

              {/* Marketing program ----------------------------------- */}
              <SectionHeading title="Marketing program" />
              <Row
                label="Marketer registration"
                value={
                  <BoolBadge enabled={data.isMarketerRegistrationOpen} />
                }
              />
              <Row
                label="Referral bonus"
                value={formatNaira(data.referralBonusAmount)}
              />
              <Row
                label="Marketer min. transaction"
                value={formatNaira(data.marketerMinTransactionAmount)}
              />
              <Row
                label="Marketer purchases needed"
                value={formatInt(data.marketerPurchasesNeeded)}
              />
              <Row
                label="Marketer total reward"
                value={formatNaira(data.marketerTotalRewardAmount)}
              />

              {/* Coupons & rewards ----------------------------------- */}
              <SectionHeading title="Coupons & rewards" />
              <Row
                label="Signup bonus amount"
                value={formatNaira(data.signupBonusAmount)}
              />
              <Row
                label="Auto-issue signup bonus coupon"
                value={<BoolBadge enabled={data.enableSignupBonusCoupon} />}
              />
              <Row
                label="Auto-issue first order discount coupon"
                value={
                  <BoolBadge enabled={data.enableFirstOrderDiscountCoupon} />
                }
              />

              {/* Platform -------------------------------------------- */}
              <SectionHeading title="Platform" />
              <Row
                label="Support phone number"
                value={data.supportPhoneNumber || "—"}
              />
              <Row
                label="Vendor incentive"
                value={formatPercent(data.vendorIncentivePercentage)}
              />
              <Row
                label="Buyer discount"
                value={formatPercent(data.buyerDiscountPercentage)}
              />
              <Row
                label="Video uploads allowed"
                value={formatInt(data.numberOfVideoUploadsAllowed)}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="bg-primary hover:bg-primaryHover text-white text-sm font-semibold px-4 py-2 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewGeneralSettingsModal;
